import React from 'react';

interface LearningPlanProgress {
  fach: string;
  learningPlansCount: number;
  totalTasks: number;
  completedTasks: number;
  progress: number;
  regularTasks: number;
  completedRegularTasks: number;
  baseProgress: number;
  bonusTasks: number;
  completedBonusTasks: number;
  bonusProgress: number;
  lpid?: number;
  learningPlanName?: string;
}

interface CircularProgressProps {
  progress: LearningPlanProgress;
  color: string;
  size?: number;
}

const CircularProgress: React.FC<CircularProgressProps> = ({ progress, color, size = 80 }) => {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  
  // Basis-Fortschritt (max 100%)
  const baseProgressClamped = Math.min(progress.baseProgress, 100);
  const baseStrokeDashoffset = circumference - (baseProgressClamped / 100) * circumference;
  
  // Bonus-Fortschritt (zusätzlich über 100%)
  const bonusProgressClamped = Math.min(progress.bonusProgress, 100); // Max 100% Bonus
  const bonusStrokeDashoffset = circumference - (bonusProgressClamped / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
        >
          {/* Hintergrundkreis */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            className="text-gray-200"
          />
          {/* Basis-Fortschrittskreis */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={baseStrokeDashoffset}
            strokeLinecap="round"
            className={`transition-all duration-300 ${color}`}
          />
          {/* Bonus-Fortschrittskreis (äußerer Ring, falls Bonus vorhanden) */}
          {progress.bonusProgress > 0 && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius - 6}
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeDasharray={circumference * 0.85} // Etwas kleiner für äußeren Ring
              strokeDashoffset={bonusStrokeDashoffset * 0.85}
              strokeLinecap="round"
              className="text-yellow-400 opacity-80"
            />
          )}
        </svg>
        {/* Prozentanzeige */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${size > 70 ? 'text-sm' : 'text-xs'} font-semibold text-gray-700`}>
            {progress.progress}%
          </span>
          {progress.bonusProgress > 0 && size > 70 && (
            <span className="text-xs text-yellow-600 font-medium">
              +{progress.bonusProgress}% ⭐
            </span>
          )}
        </div>
      </div>
      <span className="text-xs text-gray-600 mt-1 text-center max-w-[11rem] leading-tight">{progress.fach}</span>
      {progress.learningPlanName && (
        <span className="text-xs text-gray-500 mt-0.5 text-center max-w-[11rem] leading-tight line-clamp-2">
          {progress.learningPlanName}
        </span>
      )}
      <span className="text-xs text-gray-500">
        {progress.completedRegularTasks}/{progress.regularTasks}
        {progress.bonusTasks > 0 && (
          <span className="text-yellow-600"> +{progress.completedBonusTasks}/{progress.bonusTasks}⭐</span>
        )}
      </span>
      {(!progress.learningPlanName || progress.learningPlansCount !== 1) && (
        <span className="text-xs text-gray-400">
          {progress.learningPlansCount} LP
        </span>
      )}
    </div>
  );
};

export default CircularProgress;

